import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import styled, { useTheme } from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { UPDATE_ARTIST } from '@webapp/store/artist/actions';
import { releaseCheckout } from '@webapp/store/release/selectors';

import { ReleaseListSummary } from '@common/types/release/release-list-summary';

// Controllers
import ButtonLoadingCtrl from '@webapp/controllers/button-loading';
import AppleMusicCtrl from '@webapp/controllers/release/apple-music';
import StoreMaximizerCtrl from '@webapp/controllers/release/store-maximizer';
import YoutubeContentIdCtrl from '@webapp/controllers/release/youtube-content-id';

// Components
import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import { getWidth } from '@components/styles';
import Button from '@components/actions/button';
import BlueHint from '@components/basics/blue-hint';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import BoxTitleHeader from '@components/layout/box-title-header';

import CatalogReleaseInfoSidebar from '../release-info-sidebar';

interface UpgradeReleasePageProps {
  className?: string;
  summary: Partial<ReleaseListSummary>;
  isLoading: boolean;
  hasCheckout: boolean;
  clientId: string;
  subscriptionId: string;
  push: (path: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  clientId: state.artist.active?.id || '',
  subscriptionId: state.subscription.current?.id || '',
  summary: state.release.summary,
  isLoading: !!state.ui.loading[UPDATE_ARTIST],
  hasCheckout: releaseCheckout(state).some(
    (item) => item.price.value > 0 && item.quantity > 0
  ),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  push: (path: string) => dispatch(push(path)),
});

export const UpgradeReleasePage: React.FC<UpgradeReleasePageProps> = ({
  className,
  summary,
  isLoading,
  push,
  hasCheckout,
  clientId,
  subscriptionId,
}) => {
  const [releaseTitle, setReleaseTitle] = React.useState<string>('');
  const [releaseCover, setReleaseCover] = React.useState<string>('');

  const theme = useTheme();
  const match = useRouteMatch<{ artistId: string; releaseId: string }>();
  const releaseId = match.params.releaseId;
  const artistId = match.params.artistId;

  React.useEffect(() => {
    if (summary?.all?.list) {
      summary.all.list.forEach((release) =>
        release.id === releaseId
          ? (setReleaseTitle(release.title), setReleaseCover(release.cover.url))
          : null
      );
    }
  }, [releaseId, summary]);

  const handleUpgrade = () => {
    //
    push(`/plans/${clientId}/${subscriptionId}`);
  };

  const saveChanges = () => {
    //TODO
    push(`/release/${artistId}/${releaseId}/checkout/upgrade`);
  };

  return (
    <MotionOpacity className={className || ''}>
      <UpgradeReleaseWrapper>
        <CatalogReleaseInfoSidebar
          releaseTitle={releaseTitle}
          releaseCover={releaseCover}
        />

        <Header gap={10}>
          <Typography customFont="semi-bold" type="subheader">
            <Translate id="ARTIST_PROFILE.UPGRADE.RELEASE.TITLE" />
          </Typography>
          <Typography color={theme.colors.tertiary}>
            <Translate id="ARTIST_PROFILE.UPGRADE.RELEASE.SUBTITLE" />
          </Typography>
        </Header>

        <Box noBorderBottom>
          <BoxTitleHeader
            className="title-header"
            title={<Translate id="ARTIST_PROFILE.UPGRADE.RELEASE.BOX.TITLE" />}
          />
          <Grid gap={10}>
            <StoreMaximizerCtrl />
            <YoutubeContentIdCtrl />
            <AppleMusicCtrl />
          </Grid>
        </Box>
        <BlueHint hasArrow hasMarginTop onClick={handleUpgrade}>
          <Translate id="ARTIST_PROFILE.UPGRADE.RELEASE.BOX.HINT" />
        </BlueHint>

        <ButtonWrapper>
          <Button
            className="btn"
            buttonType="secondary"
            onClick={() => push('/catalog')}
          >
            <Translate id="ARTIST_PROFILE.UPGRADE.RELEASE.BUTTONS.CANCEL" />
          </Button>
          <ButtonLoadingCtrl
            className="btn"
            isLoading={!!isLoading}
            onClick={() => saveChanges()}
            // TODO: DEVTEAM-603 - hasCheckout always returns true.
            disabled={!hasCheckout}
          >
            <Translate id="ARTIST_PROFILE.UPGRADE.RELEASE.BUTTONS.CHECKOUT" />
          </ButtonLoadingCtrl>
        </ButtonWrapper>
      </UpgradeReleaseWrapper>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(UpgradeReleasePage)`
  position: relative;
  margin-top: 80px;
`);

const UpgradeReleaseWrapper = styled.div`
  min-height: 100vh;
  padding-inline: 24px;
  max-width: ${getWidth(10)};
  margin-inline: auto;

  @media ${({ theme }) => theme.device.laptopS} {
    width: auto;
    padding-inline: 0;
  }

  .title-header {
    margin-bottom: 20px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 40px;
  gap: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .btn {
    width: 100%;
    flex-grow: 1;
  }
`;

const Header = styled(Grid)`
  margin-bottom: 30px;
`;
